import axios from 'axios';
import apiClient from './apiClient';
import {
  extractBackendErrorMessage,
  isRecord,
  normalizeNullableInteger,
  normalizeNullableString,
  normalizeString,
} from './normalize';
import type { ReasoningEffort, TenantAiConfig, TenantAiConfigUpdate, TenantSummary } from './types';

/** Whitelist der zulaessigen Werte fuer `reasoningEffort` (Backend-Vertrag). */
const REASONING_EFFORT_VALUES: readonly ReasoningEffort[] = ['LOW', 'MEDIUM', 'HIGH'];

/**
 * Normalisiert `reasoningEffort`: nur bekannte Whitelist-Werte werden
 * uebernommen, jeder andere Wert (unbekannter String, falscher Typ) wird
 * zu null normalisiert statt einen kaputten Zustand durchzureichen.
 */
function normalizeReasoningEffort(value: unknown): ReasoningEffort | null {
  return typeof value === 'string' && (REASONING_EFFORT_VALUES as readonly string[]).includes(value)
    ? (value as ReasoningEffort)
    : null;
}

const WEB_SEARCH_MAX_RESULTS_MIN = 1;
const WEB_SEARCH_MAX_RESULTS_MAX = 10;

/**
 * Normalisiert `webSearchMaxResults`: nur ganze Zahlen im Vertragsbereich 1..10
 * gelten als "Websuche aktiv"; alles andere (0, 50, Float, String) wird zu null,
 * damit das Formular nicht mit einem unerklaerten Speichern-Blocker startet.
 */
function normalizeWebSearchMaxResults(value: unknown): number | null {
  const n = normalizeNullableInteger(value);
  return n !== null && n >= WEB_SEARCH_MAX_RESULTS_MIN && n <= WEB_SEARCH_MAX_RESULTS_MAX ? n : null;
}

/**
 * Service fuer die Mandanten- und KI-Konfigurations-Endpunkte des
 * MVGen-Management-Backends:
 * - GET /api/tenants                     (Mandantenliste)
 * - GET /api/tenants/{id}/ai-config       (KI-Konfiguration laden)
 * - PUT /api/tenants/{id}/ai-config       (KI-Konfiguration speichern)
 * - DELETE /api/tenants/{id}/ai-config    (KI-Konfiguration loeschen)
 *
 * Kein Caching (Projektstandard): Jeder Aufruf geht direkt ans Backend
 * (Cache-Control: no-store serverseitig).
 *
 * Alle Antworten werden defensiv normalisiert (siehe ./normalize).
 * Strukturell unbrauchbare Antworten werfen, statt still zu einem
 * Leerzustand zu werden (analog userService) — der API-Key selbst wird
 * nie uebertragen, deshalb ist eine kaputte apiKeyConfigured/apiKeyHint-
 * Angabe fachlich besonders heikel.
 */

/** Normalisiert einen Mandanten-Listeneintrag; null bei fehlender/kaputter ID. */
function normalizeTenantSummary(payload: unknown): TenantSummary | null {
  if (!isRecord(payload)) {
    return null;
  }
  const id: unknown = payload.id;
  if (typeof id !== 'string' || id === '') {
    return null;
  }
  return {
    id,
    name: normalizeString(payload.name, ''),
    slug: normalizeString(payload.slug, ''),
  };
}

/**
 * Normalisiert die KI-Konfiguration; null, wenn die Antwort strukturell
 * unbrauchbar ist oder nicht zum angefragten Mandanten gehoert (`tenantId`
 * muss exakt uebereinstimmen — sonst koennte eine vertauschte Antwort den
 * API-Key-Status eines FALSCHEN Mandanten anzeigen).
 */
function normalizeAiConfig(payload: unknown, tenantId: string): TenantAiConfig | null {
  if (!isRecord(payload) || payload.tenantId !== tenantId) {
    return null;
  }
  if (typeof payload.apiKeyConfigured !== 'boolean') {
    return null;
  }
  return {
    tenantId,
    model: normalizeNullableString(payload.model),
    apiKeyConfigured: payload.apiKeyConfigured,
    apiKeyHint: normalizeNullableString(payload.apiKeyHint),
    locationPrompt: normalizeNullableString(payload.locationPrompt),
    reasoningEffort: normalizeReasoningEffort(payload.reasoningEffort),
    webSearchMaxResults: normalizeWebSearchMaxResults(payload.webSearchMaxResults),
    updatedAt: normalizeNullableString(payload.updatedAt),
  };
}

/**
 * true, wenn der Fehler eine HTTP-404-Antwort ist (unbekannter Mandant).
 * Kapselt die axios-Abhaengigkeit, damit die Hooks sie nicht brauchen
 * (analog userService.isNotFoundError).
 */
export function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

/**
 * Liest die `message` eines Backend-Fehlerbodys (ErrorResponseDTO) aus
 * einem beliebigen Fehler, sonst die deutsche `fallback`-Meldung. Deckt
 * insbesondere HTTP 400 (Validierungsfehler) und 502 (mvgen nicht
 * erreichbar) ab, deren Meldungstext vom Backend kommt.
 */
export function extractTenantErrorMessage(error: unknown, fallback: string): string {
  return extractBackendErrorMessage(error, fallback);
}

/** Laedt die Liste aller Mandanten, sortiert nach Name (Backend-Vertrag). */
export async function fetchTenants(signal?: AbortSignal): Promise<readonly TenantSummary[]> {
  const response = await apiClient.get<unknown>('/api/tenants', { signal });
  const payload: unknown = response.data;
  if (!Array.isArray(payload)) {
    throw new Error('Unbrauchbare Antwort des Mandanten-Endpunkts');
  }
  return payload
    .map((entry: unknown) => normalizeTenantSummary(entry))
    .filter((entry): entry is TenantSummary => entry !== null);
}

/**
 * Laedt die KI-Konfiguration eines Mandanten.
 * Eine strukturell unbrauchbare Antwort wird wie ein Fehler behandelt.
 */
export async function fetchTenantAiConfig(
  tenantId: string,
  signal?: AbortSignal
): Promise<TenantAiConfig> {
  const response = await apiClient.get<unknown>(
    `/api/tenants/${encodeURIComponent(tenantId)}/ai-config`,
    { signal }
  );
  const config = normalizeAiConfig(response.data, tenantId);
  if (config === null) {
    throw new Error('Unbrauchbare Antwort des KI-Konfigurations-Endpunkts');
  }
  return config;
}

/**
 * Speichert die KI-Konfiguration eines Mandanten und liefert den vom
 * Backend BESTAETIGTEN Stand zurueck — dieser ist immer zu uebernehmen,
 * nicht der gesendete `update` (z. B. apiKeyHint aendert sich serverseitig).
 */
export async function updateTenantAiConfig(
  tenantId: string,
  update: TenantAiConfigUpdate,
  signal?: AbortSignal
): Promise<TenantAiConfig> {
  const response = await apiClient.put<unknown>(
    `/api/tenants/${encodeURIComponent(tenantId)}/ai-config`,
    update,
    { signal }
  );
  const config = normalizeAiConfig(response.data, tenantId);
  if (config === null) {
    throw new Error('Unbrauchbare Antwort des KI-Konfigurations-Endpunkts');
  }
  return config;
}

/** Loescht die KI-Konfiguration eines Mandanten (API-Key, Modell, Prompt). */
export async function deleteTenantAiConfig(tenantId: string, signal?: AbortSignal): Promise<void> {
  await apiClient.delete<unknown>(`/api/tenants/${encodeURIComponent(tenantId)}/ai-config`, {
    signal,
  });
}
